/**
 * SEO head and structured data for the privacy policy (`/privacy`).
 *
 * Constants only, like every other `src/lib/*-seo.ts`: the route tree imports
 * each route file eagerly for its `head()`, so whatever this module imports
 * lands in the entry chunk of every page. The Markdown source and its renderer
 * live in `privacy-policy.ts`, imported from the route's code-split component
 * alone.
 */

import { isProduction } from "@/config/config";
import { PUBLISHER_NODE, SITE_URL, WEBSITE_NODE } from "@/lib/marketing-jsonld";

export const PRIVACY_URL = `${SITE_URL}privacy`;

/** Two to four words: the brief's requirement for the document title. */
export const PRIVACY_DOCUMENT_TITLE = "GoodSender Privacy Policy";

export const PRIVACY_DESCRIPTION =
  "How GoodSender collects, uses, shares and keeps information for your account, your recipients, and the Claude MCP connector.";

const PRIVACY_OG_IMAGE = "https://goodsender.com/og-image.png";

/**
 * The policy's "Last updated" day, YYYY-MM-DD. A literal, not a parse of the
 * Markdown: this module is on the eager route-tree path (see the docblock),
 * so it must not import the policy text or a parser. `privacy-policy-page.test.ts`
 * asserts it equals `policyLastUpdated(policy)`, so a policy revision that
 * forgets this line fails CI instead of shipping a stale sitemap lastmod.
 */
export const PRIVACY_LAST_UPDATED = "2026-11-02";

export const privacyJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${PRIVACY_URL}#privacy`,
  url: PRIVACY_URL,
  name: PRIVACY_DOCUMENT_TITLE,
  description: PRIVACY_DESCRIPTION,
  inLanguage: "en",
  dateModified: PRIVACY_LAST_UPDATED,
  isPartOf: WEBSITE_NODE,
  publisher: PUBLISHER_NODE,
};

export const privacyBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Privacy", item: PRIVACY_URL },
  ],
};

type HeadElement = { type: "meta" | "link"; props: Record<string, string> };

const PRIVACY_SEO_HEAD_ELEMENTS: HeadElement[] = [
  { type: "meta", props: { name: "description", content: PRIVACY_DESCRIPTION } },
  {
    type: "meta",
    props: {
      name: "robots",
      content: isProduction
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    },
  },
  { type: "meta", props: { name: "author", content: "Joy Labs Ventures" } },
  { type: "link", props: { rel: "canonical", href: PRIVACY_URL } },
  // Open Graph
  { type: "meta", props: { property: "og:type", content: "website" } },
  { type: "meta", props: { property: "og:url", content: PRIVACY_URL } },
  { type: "meta", props: { property: "og:title", content: PRIVACY_DOCUMENT_TITLE } },
  { type: "meta", props: { property: "og:description", content: PRIVACY_DESCRIPTION } },
  { type: "meta", props: { property: "og:image", content: PRIVACY_OG_IMAGE } },
  { type: "meta", props: { property: "og:image:width", content: "1200" } },
  { type: "meta", props: { property: "og:image:height", content: "630" } },
  { type: "meta", props: { property: "og:site_name", content: "GoodSender" } },
  { type: "meta", props: { property: "og:locale", content: "en_US" } },
  // Twitter Card
  { type: "meta", props: { name: "twitter:card", content: "summary_large_image" } },
  { type: "meta", props: { name: "twitter:title", content: PRIVACY_DOCUMENT_TITLE } },
  { type: "meta", props: { name: "twitter:description", content: PRIVACY_DESCRIPTION } },
  { type: "meta", props: { name: "twitter:image", content: PRIVACY_OG_IMAGE } },
];

/** Route-level `head()` for /privacy (same contract as the other marketing heads). */
export function privacyRouteHead() {
  return {
    meta: [
      { title: PRIVACY_DOCUMENT_TITLE },
      ...PRIVACY_SEO_HEAD_ELEMENTS.filter((e) => e.type === "meta").map((e) => e.props),
    ],
    links: PRIVACY_SEO_HEAD_ELEMENTS.filter((e) => e.type === "link").map((e) => e.props),
  };
}
